
import React from 'react';
import { Card, CardHeader, CardContent, CardFooter, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Check, ArrowRight, Sparkles, Zap, Rocket } from 'lucide-react';
import { productDescriptions } from '@/components/ProductDetails';

interface UpsellCardProps {
  productId: string;
  price: number;
  title: string;
  subtitle: string;
  isAdded: boolean;
  onAdd: () => void;
  onSkip: () => void;
  currentTotal: number;
}

const BlinkingArrow = () => (
  <div className="animate-pulse ml-2">
    <ArrowRight className="h-5 w-5 text-white font-extrabold" />
  </div>
);

const UpsellCard: React.FC<UpsellCardProps> = ({
  productId,
  price,
  title,
  subtitle,
  isAdded,
  onAdd,
  onSkip,
  currentTotal
}) => {
  const getUpsellIcon = (id: string) => {
    switch(id) {
      case 'fast-track': return <Zap className="h-8 w-8 text-orange-500" />;
      case 'accelerator': return <Sparkles className="h-8 w-8 text-blue-500" />;
      case 'business-mastery': return <Sparkles className="h-8 w-8 text-purple-500" />;
      case 'launch-guide': return <Rocket className="h-8 w-8 text-indigo-500" />;
      default: return <Zap className="h-8 w-8 text-gray-500" />;
    }
  };

  const getHeaderGradient = (id: string) => {
    switch(id) {
      case 'fast-track': 
        return "bg-gradient-to-r from-orange-500 to-amber-500";
      case 'accelerator': 
        return "bg-gradient-to-r from-blue-500 to-indigo-600";
      case 'business-mastery': 
        return "bg-gradient-to-r from-purple-600 to-pink-500";
      case 'launch-guide': 
        return "bg-gradient-to-r from-indigo-500 to-violet-600";
      default: 
        return "bg-gradient-to-r from-gray-600 to-slate-700";
    }
  };

  // Updated button gradient to be green
  const getButtonGradient = (id: string) => {
    return "bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 animate-[pulse_1.5s_infinite]";
  };

  const potentialTotal = isAdded ? currentTotal : currentTotal + price;

  return (
    <div className="max-w-4xl mx-auto">
      <div className="rounded-xl overflow-hidden shadow-lg bg-white">
        <div className={`p-6 ${getHeaderGradient(productId)} text-white flex items-center gap-4`}>
          <div className="bg-white/20 rounded-full p-3">
            {getUpsellIcon(productId)}
          </div>
          <div>
            <h1 className="text-2xl md:text-3xl font-bold">{title}</h1>
            <p className="text-white/90 text-lg">{subtitle}</p>
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 p-6">
          <div className="md:col-span-2">
            {productDescriptions[productId] || <p>No description available</p>}
          </div>
          
          <div className="md:col-span-1">
            <Card className="shadow-md">
              <CardHeader className={`${getHeaderGradient(productId)} text-white py-4`}>
                <CardTitle className="flex items-center gap-2 text-xl">Special Offer</CardTitle>
                <CardDescription className="text-white/90">
                  Add to your purchase
                </CardDescription>
              </CardHeader>
              <CardContent className="pt-6">
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <span className="font-semibold">Original Price:</span>
                    <span className="font-bold">${price}</span>
                  </div>
                  
                  <div className="flex justify-between items-center">
                    <span className="font-semibold">Current Total:</span>
                    <span className="font-bold">${currentTotal}</span>
                  </div>
                  
                  <div className="flex justify-between items-center text-lg">
                    <span className="font-semibold">New Total:</span>
                    <span className="font-bold">${potentialTotal}</span>
                  </div>
                </div>
              </CardContent>
              <CardFooter className="flex flex-col gap-3">
                <Button 
                  className={`w-full py-6 text-lg ${getButtonGradient(productId)}`}
                  onClick={onAdd}
                  disabled={isAdded}
                >
                  {isAdded ? 'Already Added' : 'Yes, Add This!'}
                  {!isAdded && <BlinkingArrow />}
                </Button>
                
                <Button 
                  variant="outline" 
                  className="w-full" 
                  onClick={onSkip}
                >
                  No Thanks, Continue
                </Button>
              </CardFooter>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UpsellCard;
